<template>
  <section class="admin-content" id="system-health">
    <Navbar />
    <main class="admin-main">
      <div class="page-header">
        <div class="container">
          <div class="d-flex align-items-center justify-content-between flex-wrap text-white" style="padding: 32px 0; gap: 12px;">
            <div class="d-flex align-items-center">
              <div class="header-icon-wrap mr-3"><i class="fe fe-heart" style="font-size: 22px;"></i></div>
              <div>
                <h4 class="mb-0 font-weight-bold">System Health</h4>
                <small style="opacity: 0.8;">Daily data checks, sent to the agency admins every morning</small>
              </div>
            </div>
            <div class="d-flex align-items-center" style="gap: 8px;">
              <select v-if="runs.length" class="form-control form-control-sm" style="min-width: 220px;" v-model="runId" @change="loadRun">
                <option v-for="r in runs" :key="r.id" :value="r.id">{{ r.started_at }} · {{ r.status }}</option>
              </select>
              <div v-if="isAdmin" class="run-wrap">
                <button class="btn filter-toggle-btn" @click="runMenu = !runMenu" :disabled="running">
                  <span v-if="running && runMode === 'now'"><span class="spinner-border spinner-border-sm mr-1"></span> Checking…</span>
                  <span v-else><i class="fe fe-play mr-1"></i> {{ running ? "Queued…" : "Run health check" }} <i class="fe fe-chevron-down ml-1"></i></span>
                </button>
                <div v-if="runMenu" class="run-menu" @mouseleave="runMenu = false">
                  <button class="run-option" @click="runNow('now')"><b><i class="fe fe-zap mr-1"></i> Run now</b><small>Wait for the result here (a minute or two).</small></button>
                  <button class="run-option" @click="runNow('queue')"><b><i class="fe fe-clock mr-1"></i> Run in the background</b><small>Refresh this page in a few minutes.</small></button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <section style="margin-top: -20px;">
        <div class="container pb-5">
          <div v-if="loading" class="card shadow-sm table-card"><div class="empty-state"><i class="mdi mdi-loading mdi-spin"></i>Loading…</div></div>

          <div v-else-if="!run" class="card shadow-sm table-card">
            <div class="empty-state">
              <i class="fe fe-heart"></i>
              No health check has run yet. It runs every morning{{ isAdmin ? "; you can also run it now." : "." }}
            </div>
          </div>

          <template v-else>
            <div v-if="run.status === 'failed'" class="alert alert-danger">This check failed: {{ run.error }}</div>
            <div v-if="run.status === 'running'" class="alert alert-info">This check is still running. Refresh in a few minutes.</div>

            <div class="row">
              <div class="col-md-4 col-6" v-for="tile in tiles" :key="tile.label">
                <div class="stat-card">
                  <div class="stat-icon-wrap" :style="{ background: tile.bg }"><i :class="tile.icon" :style="{ color: tile.color }"></i></div>
                  <div><p class="stat-label">{{ tile.label }}</p><h3 class="stat-value">{{ tile.value }}</h3><p class="stat-sub">{{ tile.sub }}</p></div>
                </div>
              </div>
            </div>

            <div class="card shadow-sm table-card mb-4">
              <div class="form-card-header">
                <div class="section-number"><i class="fe fe-list" style="font-size: 12px;"></i></div>
                <h6 class="mb-0 font-weight-bold text-white">Checks</h6>
              </div>
              <div class="table-responsive">
                <table class="table mb-0">
                  <thead class="table-head">
                    <tr><th class="table-th">Check</th><th class="table-th">Severity</th><th class="table-th">Records</th><th class="table-th">Since last check</th><th class="table-th"></th></tr>
                  </thead>
                  <tbody>
                    <tr class="table-row" v-for="c in checks" :key="c.key" :class="{ 'table-active': c.key === activeCheck }">
                      <td><b>{{ c.label }}</b><div class="small text-muted">{{ c.hint }}</div></td>
                      <td><span class="sev" :class="'sev-' + c.severity">{{ c.severity }}</span></td>
                      <td><b>{{ count(c.key) | numeral(0, 0) }}</b></td>
                      <td>
                        <span v-if="delta(c.key) === null" class="muted">—</span>
                        <span v-else-if="delta(c.key) === 0" class="muted">no change</span>
                        <span v-else :class="delta(c.key) > 0 ? 'delta-up' : 'delta-down'">{{ delta(c.key) > 0 ? "+" : "" }}{{ delta(c.key) | numeral(0, 0) }}</span>
                      </td>
                      <td class="text-right">
                        <button v-if="count(c.key)" class="btn btn-sm btn-outline-primary" @click="openCheck(c.key)">View records</button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div v-if="activeCheck" class="card shadow-sm table-card mb-4" ref="findings">
              <div class="form-card-header">
                <div class="section-number"><i class="fe fe-search" style="font-size: 12px;"></i></div>
                <div class="flex-grow-1">
                  <h6 class="mb-0 font-weight-bold text-white">{{ checkLabel(activeCheck) }}</h6>
                  <small style="color: rgba(255,255,255,.75);">Up to 200 records are kept per check; the count above is the full number.</small>
                </div>
                <select class="form-control form-control-sm" style="max-width: 160px;" v-model="resolvedFilter" @change="loadFindings(1)">
                  <option value="">All</option>
                  <option value="0">Open</option>
                  <option value="1">Resolved</option>
                </select>
              </div>
              <p v-if="findings.data.length && findings.data[0].details && findings.data[0].details.all_values" class="small text-muted px-3 pt-3 mb-0">
                Values found (all records): {{ findings.data[0].details.all_values }}
              </p>
              <div class="table-responsive">
                <table class="table mb-0">
                  <thead class="table-head">
                    <tr><th class="table-th">Record</th><th class="table-th">Details</th><th class="table-th">Status</th><th class="table-th"></th></tr>
                  </thead>
                  <tbody>
                    <tr v-if="!findings.data.length"><td colspan="4" class="empty-state">Nothing to show.</td></tr>
                    <tr class="table-row" v-for="f in findings.data" :key="f.id">
                      <td><router-link :to="subjectLink(f)">{{ subjectLabel(f) }}</router-link></td>
                      <td class="small">{{ describe(f) }}</td>
                      <td>
                        <span v-if="f.resolved_at" class="sev sev-low">Resolved {{ f.resolved_at.substr(0, 10) }}</span>
                        <span v-else class="sev sev-medium">Open</span>
                      </td>
                      <td class="text-right">
                        <button v-if="isAdmin" class="btn btn-sm" :class="f.resolved_at ? 'btn-outline-secondary' : 'btn-outline-success'" @click="resolve(f, !f.resolved_at)">
                          {{ f.resolved_at ? "Reopen" : "Mark resolved" }}
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div class="card-footer bg-white d-flex justify-content-between align-items-center" v-if="findings.last_page > 1">
                <small class="text-muted">Page {{ findings.current_page }} of {{ findings.last_page }} · {{ findings.total }} records</small>
                <div>
                  <button class="btn btn-sm btn-outline-secondary mr-1" :disabled="findings.current_page <= 1" @click="loadFindings(findings.current_page - 1)">Previous</button>
                  <button class="btn btn-sm btn-outline-secondary" :disabled="findings.current_page >= findings.last_page" @click="loadFindings(findings.current_page + 1)">Next</button>
                </div>
              </div>
            </div>
          </template>
        </div>
      </section>
    </main>
  </section>
</template>

<script>
import Navbar from "@/views/Navbar.vue";

/** Daily health check results (GET health/runs, health/runs/{id}, findings; admins: run now, resolve). */
export default {
  components: { Navbar },
  data() {
    const user = JSON.parse(localStorage.getItem("user") || "{}") || {};
    return {
      user,
      isAdmin: user.type === "shis" || String(user.user_role) === "1",
      loading: true,
      running: false,
      runMenu: false,
      runMode: "now",
      runs: [],
      runId: null,
      run: null,
      previous: null,
      checks: [],
      activeCheck: null,
      resolvedFilter: "",
      findings: { data: [], current_page: 1, last_page: 1, total: 0 },
    };
  },
  computed: {
    tiles() {
      const issues = this.checks.filter((c) => c.severity !== "info").reduce((n, c) => n + this.count(c.key), 0);
      const high = this.checks.filter((c) => c.severity === "high").reduce((n, c) => n + this.count(c.key), 0);
      return [
        { label: "Records to review", value: issues.toLocaleString(), sub: "All checks except information", icon: "fe fe-alert-triangle", bg: "#fff7ed", color: "#ea580c" },
        { label: "High severity", value: high.toLocaleString(), sub: "Duplicate IDs, no facility, stale codes", icon: "fe fe-alert-octagon", bg: "#fef2f2", color: "#dc2626" },
        { label: "Checked", value: this.run ? this.run.started_at : "—", sub: this.run && this.run.recipients ? `Sent to ${this.run.recipients} admin(s)` : "Not sent", icon: "fe fe-clock", bg: "#eff6ff", color: "#2563eb" },
      ];
    },
  },
  mounted() {
    this.axios.get("/api/v1/auth/health/checks").then((res) => { this.checks = res.data.data; });
    this.loadRuns();
  },
  methods: {
    loadRuns() {
      this.loading = true;
      this.axios
        .get("/api/v1/auth/health/runs")
        .then((res) => {
          this.runs = res.data.data;
          if (this.runs.length) {
            this.runId = this.runs[0].id;
            return this.loadRun();
          }
        })
        .catch(this.fail)
        .finally(() => { this.loading = false; });
    },
    loadRun() {
      this.activeCheck = null;
      return this.axios.get(`/api/v1/auth/health/runs/${this.runId}`).then((res) => {
        this.run = res.data.data;
        this.previous = res.data.previous_counts;
      });
    },
    count(key) {
      return this.run && this.run.counts ? Number(this.run.counts[key] || 0) : 0;
    },
    delta(key) {
      if (!this.previous) return null;
      return this.count(key) - Number(this.previous[key] || 0);
    },
    checkLabel(key) {
      const c = this.checks.find((x) => x.key === key);
      return c ? c.label : key;
    },
    openCheck(key) {
      this.activeCheck = key;
      this.resolvedFilter = "";
      this.loadFindings(1).then(() => this.$nextTick(() => this.$refs.findings && this.$refs.findings.scrollIntoView({ behavior: "smooth" })));
    },
    loadFindings(page) {
      return this.axios
        .get(`/api/v1/auth/health/runs/${this.runId}/findings`, { params: { check: this.activeCheck, page, resolved: this.resolvedFilter || undefined } })
        .then((res) => { this.findings = res.data; })
        .catch(this.fail);
    },
    resolve(f, resolved) {
      this.axios
        .post(`/api/v1/auth/health/findings/${f.id}/resolve`, { resolved })
        .then((res) => { Object.assign(f, res.data.data); })
        .catch(this.fail);
    },
    // mode "now": runs during the request and opens the new result; "queue": runs on the queue
    runNow(mode) {
      this.runMenu = false;
      this.runMode = mode;
      this.running = true;
      this.axios
        .post("/api/v1/auth/health/run", { send: false, mode }, { timeout: 0 })
        .then((res) => {
          this.$toasted.success(res.data.message, { position: "top-center", duration: 6000 });
          if (mode === "now") {
            this.running = false;
            this.loadRuns(); // the newest run (the one just made) opens first
          }
        })
        .catch((e) => { this.running = false; this.fail(e); });
    },
    subjectLink(f) {
      const id = f.subject_id;
      return { user: `/client/${id}`, dependant: `/dependent/${id}`, claim: `/claim/${id}`, authorization_code: `/authorization-code/${id}` }[f.subject_type] || "#";
    },
    subjectLabel(f) {
      const d = f.details || {};
      const name = d.name || (d.people && d.people[0] && d.people[0].name) || d.claim || d.facility;
      const kind = { user: "Enrollee", dependant: "Dependant", claim: "Claim", authorization_code: "Authorization code" }[f.subject_type] || f.subject_type;
      return name ? `${name}` : `${kind} #${f.subject_id}`;
    },
    describe(f) {
      const d = f.details || {};
      const parts = [];
      if (d.id_card_number) parts.push(`ID ${d.id_card_number}`);
      if (d.count) parts.push(`${d.count} records share this`);
      if (d.people) parts.push(d.people.map((p) => p.name).join(", "));
      if (d.phone_number) parts.push(`phone ${d.phone_number}`);
      if (d.dob) parts.push(`born ${d.dob}`);
      if (d.missing) parts.push(`missing: ${d.missing.join(", ")}`);
      if ("gender" in d) parts.push(d.gender ? `gender "${d.gender}"` : "gender empty");
      if (d.expiry_date) parts.push(`expires ${d.expiry_date}`);
      if (d.requested_at) parts.push(`requested ${d.requested_at}`);
      if (d.submitted_at) parts.push(`submitted ${d.submitted_at}`);
      if (d.facility && f.subject_type !== "user") parts.push(d.facility);
      if (d.total_cost) parts.push(`₦${Number(d.total_cost).toLocaleString()}`);
      return parts.join(" · ") || "—";
    },
    fail(e) {
      const msg = (e.response && e.response.data && e.response.data.message) || "Something went wrong.";
      this.$toasted.error(msg, { position: "top-center", duration: 4000 });
    },
  },
};
</script>

<style scoped>
.run-wrap { position: relative; }
.run-menu { position: absolute; right: 0; top: 100%; z-index: 1050; margin-top: 4px; background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; box-shadow: 0 10px 30px rgba(15, 23, 42, 0.15); width: 280px; padding: 6px; }
.run-option { display: block; width: 100%; text-align: left; background: none; border: 0; border-radius: 8px; padding: 8px 10px; color: #1e293b; }
.run-option:hover { background: #f1f5f9; }
.run-option small { display: block; color: #64748b; font-size: 0.75rem; margin-top: 2px; }
@import "./reports.css";
</style>
